import { css } from '@emotion/react'
import { graphql, useStaticQuery } from 'gatsby'
import {
  ComponentPropsWithoutRef,
  Fragment,
  useMemo,
  useState,
} from 'react'

import { AlumnaeArticleThumbnail } from '@/features/alumnae-sections'
import { mq } from '@/theme/mixins'
import { colors } from '@/theme/variables'

import { NewsArticleThumbnail } from '../NewsArticleThumbnail/NewsArticleThumbnail'
import { NewsFilters } from './NewsFilters'

export const NewsGrid = ({
  ...props
}: ComponentPropsWithoutRef<'section'>): JSX.Element => {
  const {
    allDatoCmsNewsArticle: { nodes: newsArticles },
    allDatoCmsAlumnaeArticle: { nodes: alumnaeArticles },
  } = useStaticQuery<Queries.NewsGridQuery>(graphql`
    query NewsGrid {
      allDatoCmsNewsArticle(sort: { meta: { createdAt: DESC } }) {
        nodes {
          ...NewsArticleThumbnail
        }
      }
      allDatoCmsAlumnaeArticle(sort: { meta: { createdAt: DESC } }) {
        nodes {
          ...AlumnaeArticle
          thumbnailImage {
            gatsbyImageData(
              width: 480
              sizes: "(max-width: 540px) 100vw, (max-width: 720px) 50vw, (max-width: 1080px) 33vw, 25vw"
              imgixParams: {
                ar: "3:2"
                fit: "crop"
                crop: "focalpoint"
              }
            )
            ...ImageFocalData
          }
        }
      }
    }
  `)

  const [filters, setFilters] = useState<{
    category?: string | null
    division?: string | null
  }>({
    category: null,
    division: null,
  })

  const filteredArticles = useMemo(() => {
    const articles = [...newsArticles, ...alumnaeArticles].sort(
      (a, b) => {
        const dateA = new Date(a.meta.createdAt || '')
        const dateB = new Date(b.meta.createdAt || '')
        return dateB.getTime() - dateA.getTime()
      }
    )
    return articles.reduce(
      (acc, article) => {
        switch (article.__typename) {
          case 'DatoCmsNewsArticle':
            if (
              (!filters.category ||
                filters.category === article.category?.name) &&
              (!filters.division ||
                filters.division ===
                  article.division?.[0]?.divisionName)
            ) {
              acc.push(article)
            }
            break
          case 'DatoCmsAlumnaeArticle':
            if (
              (filters.category === null &&
                filters.division === null) ||
              filters.category === 'Alumnae News'
            ) {
              acc.push(article)
            }
            break
        }
        return acc
      },
      [] as (
        | Queries.NewsArticleThumbnailFragment
        | Queries.AlumnaeArticleThumbnailFragment
      )[]
    )
  }, [newsArticles, alumnaeArticles, filters])

  const styles = {
    grid: css`
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: var(--gtr-36);
      align-items: flex-start;
      background: #fff;
      padding: var(--row-36) var(--margin) var(--row-72);
      ${mq().ml} {
        grid-template-columns: repeat(3, 1fr);
      }
      ${mq().ms} {
        grid-template-columns: 1fr 1fr;
      }
      ${mq().s} {
        grid-template-columns: 1fr;
      }
    `,
    filters: css``,
    error: css`
      font-size: var(--fs-48);
      color: ${colors.gray45};
      text-align: center;
      grid-column: 1 / -1;
    `,
  }
  return (
    <Fragment>
      <NewsFilters
        filters={filters}
        setFilters={setFilters}
        css={styles.filters}
      />
      <section
        css={styles.grid}
        {...props}
      >
        {filteredArticles.map(article => {
          switch (article.__typename) {
            case 'DatoCmsNewsArticle':
              return (
                <NewsArticleThumbnail
                  data={article}
                  key={article.id}
                  layout="PAGE"
                  headingLevel={2}
                />
              )
            case 'DatoCmsAlumnaeArticle':
              return (
                <AlumnaeArticleThumbnail
                  data={article}
                  key={article.id}
                  layout="NEWS_PAGE"
                  headingLevel={2}
                />
              )
          }
        })}
        {filteredArticles.length === 0 && (
          <h2 css={styles.error}>
            Sorry, there are no articles that match your query.
          </h2>
        )}
      </section>
    </Fragment>
  )
}
